import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import NotificationBell from "@/components/NotificationBell";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { Moon, Sun, LogOut, Menu, LayoutDashboard, GraduationCap, Users, Building2, UserSquare2, Award, FileBarChart, Settings, Calendar, ShieldCheck, KeyRound, BookOpen, Lock } from "lucide-react";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/trainings", label: "Trainings", icon: GraduationCap },
  { to: "/calendar", label: "Calendar", icon: Calendar },
  { to: "/certificates", label: "Certificates", icon: Award },
  { to: "/employees", label: "Employees", icon: Users },
  { to: "/masters", label: "Masters", icon: Building2 },
  { to: "/reports", label: "Reports", icon: FileBarChart },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/documentation", label: "Documentation (Tutorial)", icon: BookOpen },
];

// The API refuses these to everybody but an Admin, so offering them to anyone else is a
// link whose only possible outcome is a refusal.
const adminNav = [
  { to: "/users", label: "Users", icon: KeyRound },
  { to: "/audit", label: "Audit Log", icon: ShieldCheck },
];

// Rendered twice, like NavLinks: the fixed sidebar and the drawer. The Change password
// button lives here rather than in the header, which already carries four controls and
// is the tightest row on a 390px screen.
function SignedInAs({ user, onChangePassword, prefix = "nav" }) {
  return (
    <div className="p-3 shrink-0 border-t border-border">
      <div className="text-xs text-muted-foreground">Signed in as</div>
      <div className="text-sm font-medium truncate">{user?.name}</div>
      <div className="text-xs overline text-primary mt-0.5">{user?.role}</div>
      <Button variant="ghost" size="sm" className="w-full justify-start mt-2 -ml-2 h-8 px-2 text-muted-foreground"
              onClick={onChangePassword} data-testid={`${prefix}-change-pw`}>
        <Lock className="w-3.5 h-3.5 mr-1.5" /> Change password
      </Button>
    </div>
  );
}

// One definition, rendered twice: the fixed sidebar on a wide screen and the drawer
// on a narrow one. `prefix` keeps the two sets of test ids apart while both are mounted.
function NavLinks({ prefix = "nav", onNavigate }) {
  const { user } = useAuth();
  return [...nav, ...(isAdmin(user) ? adminNav : [])].map(({ to, label, icon: Icon }) => (
    <NavLink
      key={to}
      to={to}
      end={to === "/"}
      onClick={onNavigate}
      data-testid={`${prefix}-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
          isActive ? "bg-primary text-primary-foreground font-medium" : "text-foreground/70 hover:bg-muted hover:text-foreground"
        }`
      }
    >
      <Icon className="w-4 h-4" aria-hidden="true" />
      {label}
    </NavLink>
  ));
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const nav_ = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    /* The shell is exactly one viewport tall and does not scroll; the CONTENT does.
       It used to be `min-h-screen`, so the document grew with the page and the whole
       thing scrolled together — the sidebar slid up out of sight on any long register,
       leaving no navigation until you scrolled back to the top. The header survived only
       because it was `sticky`, which is a patch over the same problem.

       100dvh via an inline style, with h-screen's 100vh behind it: on a phone `vh` is the
       tallest the viewport ever gets, so a fixed-height shell measured in vh hides its own
       last rows behind the address bar. A browser that does not know `dvh` drops the
       inline declaration and keeps the class. */
    <div className="h-screen flex bg-background overflow-hidden" style={{ height: "100dvh" }}>
      <aside className="w-60 shrink-0 border-r border-border bg-card hidden md:flex flex-col" data-testid="app-sidebar">
        <div className="h-16 shrink-0 flex items-center gap-2 px-5 border-b border-border">
          <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold">A</div>
          <div>
            <div className="font-heading font-bold text-sm leading-tight">Abhyas</div>
            <div className="text-[10px] text-muted-foreground overline">Shakun Polymers</div>
          </div>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-0.5">
          <NavLinks />
        </nav>
        <SignedInAs user={user} onChangePassword={() => setPwOpen(true)} />
      </aside>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-64 p-0 flex flex-col" data-testid="mobile-nav">
          <div className="h-16 flex items-center gap-2 px-5 border-b border-border">
            <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold">A</div>
            <div>
              <SheetTitle className="font-heading font-bold text-sm leading-tight">Abhyas</SheetTitle>
              <div className="text-[10px] text-muted-foreground overline">Shakun Polymers</div>
            </div>
          </div>
          <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
            <NavLinks prefix="mnav" onNavigate={() => setMenuOpen(false)} />
          </nav>
          <SignedInAs user={user} prefix="mnav"
                      onChangePassword={() => { setMenuOpen(false); setPwOpen(true); }} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        {/* No longer sticky: it is a fixed row of a column that does not scroll, which
            is the same result without the stacking-context cost. */}
        <header className="h-16 shrink-0 glass-header z-30 flex items-center justify-between px-6" data-testid="app-header">
          <div className="flex items-center gap-2 min-w-0">
            {/* The sidebar is hidden below md. Without this there was no way at all to
                leave the page you landed on: ten nav links in the DOM, none reachable. */}
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMenuOpen(true)}
                    data-testid="mobile-menu-btn" aria-label="Open navigation">
              <Menu className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <div className="overline text-muted-foreground">HR / Training</div>
              <h1 className="font-heading font-bold text-lg leading-tight truncate">Abhyas</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="outline" size="sm" onClick={async () => { await logout(); nav_("/login"); }} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </header>
        {/* The one thing that scrolls. The padding and the max-width moved inside it so
            the scrollbar sits at the edge of the window rather than inside the centred
            column, and so `sticky` inside a page (the attendance Save bar) resolves
            against this scrollport. */}
        <main className="flex-1 min-h-0 overflow-y-auto" data-testid="app-scroll">
          <div className="p-4 sm:p-6 max-w-[1600px] w-full mx-auto">{children}</div>
        </main>
      </div>

      {/* Never forced here: SetPassword.jsx stands in front of Protected for every role,
          so an account that must change its password never reaches this shell. */}
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
